import React, { memo, useState } from 'react';
import { Button, Link, Stack } from '@mui/material';
import { useDispatch } from 'react-redux';
import SaveIcon from '@mui/icons-material/Save';

import { CardWrapper, FormFieldUpload } from '@monorepo/common';

import { ArrowRight as ArrowRightIcon } from '../../../../components/Icons/arrow-right';
import { usePostLogoMutation } from '../../../../services/servicePostAJob';
import { showNotification } from '../../../../features/sliceNotification';
import { IEventsStepProps } from '../../interfaces';
import { IStep4Form } from './formInterface';
import { cloudfrontImagesBaseURL } from '../../../../components/JobStepper/staticValues';
import UploadLogoTitle from '../../../../components/JobStepper/components/UploadLogoTitle';

interface IStep5Props extends IEventsStepProps {
  defaultData:IStep4Form,
  setStep4: (res:IStep4Form) => void;
  onSave?: (res:IStep4Form) => void;
  onUploadPendingChange?: (pending: boolean) => void;
}
export const Step4 = memo(({
  onNext,
  onBack,
  onSave,
  defaultData,
  setStep4,
  onUploadPendingChange,
  Nav,
}:IStep5Props) => {
  const dispatch = useDispatch();
  const [postLogo, { isLoading }] = usePostLogoMutation();
  const [uploadAttempt, setUploadAttempt] = useState(0);

  const change = async (file: File | File[]) => {
    if (!Array.isArray(file)) {
      const formData = new FormData();
      formData.append('logo', file);
      onUploadPendingChange?.(true);
      try {
        const res = await postLogo(formData);
        if (res && 'data' in res) {
          const { url } = res.data as { url: string };
          setStep4({
            ...defaultData,
            uploadedLogoPath: `${cloudfrontImagesBaseURL}${url}`
          });
          dispatch(showNotification({
            type: 'success',
            show: true,
            text: 'Logo was uploaded!'
          }));
          return;
        }
      } catch {
        // RTK normally resolves an error result; keep the same behavior if it rejects.
      } finally {
        onUploadPendingChange?.(false);
      }
      // Restore the saved preview after an unsuccessful upload. A failed attempt
      // must not cause Next or Save to clear the listing's existing logo.
      setUploadAttempt((attempt) => attempt + 1);
      dispatch(showNotification({
        type: 'error',
        show: true,
        text: 'Logo not saved!'
      }));
    }
  };
  const onRemove = (status: boolean) => {
    if (status) {
      setStep4({
        ...defaultData,
        uploadedLogoPath: null
      });
    }
  };

  const next = () => {
    if (onNext) {
      onNext();
    }
  };

  const save = () => {
    setStep4(defaultData);
    onSave?.(defaultData);
  };

  return (
    <CardWrapper
      classNameForInternalBlock="cpg-step-card"
      isLoading={isLoading}
      dataCy="step4"
    >
      <UploadLogoTitle />

      <FormFieldUpload
        key={uploadAttempt}
        onChange={change}
        maxFilesUpload={1}
        onRemove={onRemove}
        removeLabel="Remove logo"
        classNameForWrap="cpg-dropzone"
        classNameForImage="cpg-logo-preview"
        maxSizeBytes={10485760} // max file size 10mb
        defaultValue={defaultData?.uploadedLogoPath && defaultData.uploadedLogoPath}
        uploadAreaSettings={{
          image: (
            <span
              className="cpg-dropzone__icon"
              aria-hidden="true"
            >
              <svg
                width="26"
                height="26"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M12 15V4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M7 9l5-5 5 5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M5 15v3a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-3"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          ),
          mainTitle: (
            <>
              Drag & drop your logo or
              {' '}
              <Link underline="always">Browse your files</Link>
            </>
          ),
          mainSubtitleTitle: (
            <span className="cpg-dropzone__hint">PNG or JPG · up to 10 MB</span>
          )
        }}
      />

      <Nav
        nextDisabled={isLoading}
        onBack={onBack}
        nextButton={(
          <Stack
            className="cpg-edit-step-actions"
            direction={{ xs: 'column', sm: 'row' }}
          >
            <Button
              startIcon={<SaveIcon />}
              variant="outlined"
              disabled={isLoading}
              type="button"
              onClick={save}
              data-cy="saveListing"
            >
              Save Listing
            </Button>
            <Button
              endIcon={(<ArrowRightIcon fontSize="small" />)}
              variant="contained"
              data-cy="nextStep"
              disabled={isLoading}
              type="button"
              onClick={next}
            >
              Next Step
            </Button>
          </Stack>
            )}
      />
    </CardWrapper>
  );
});
